// frontend/src/store/useProjectStore.js
import { create } from 'zustand';
import { apiClient } from '../api/client';

export const useProjectStore = create((set, get) => ({

  // ── Estado ────────────────────────────────────────────────────────
  projects: [],
  currentProject: null,   // proyecto con fases anidadas (GET /{id})
  runs: [],
  benchmark: null,

  isLoadingList: false,
  isLoadingDetail: false,
  isLoadingRuns: false,
  isLoadingBenchmark: false,
  isSaving: false,
  error: null,
  allPipelines: [],
  isFetchingPipelines: false,

  // ── UI local ──────────────────────────────────────────────────────
  expandedPhases: new Set(),   // idProjectPhase abiertos en el acordeón

  // ─────────────────────────────────────────────────────────────────
  // PROJECTS
  // ─────────────────────────────────────────────────────────────────

  fetchProjects: async () => {
    set({ isLoadingList: true, error: null });
    try {
      const res = await apiClient.get('/projects/');
      set({ projects: res.data, isLoadingList: false });
    } catch (err) {
      set({ error: err.message, isLoadingList: false });
    }
  },

  fetchProjectDetail: async (idProject) => {
    set({ isLoadingDetail: true, error: null, currentProject: null });
    try {
      const res = await apiClient.get(`/projects/${idProject}`);
      set({ currentProject: res.data, isLoadingDetail: false });
    } catch (err) {
      set({ error: err.message, isLoadingDetail: false });
    }
  },

  createProject: async (payload) => {
    set({ isSaving: true, error: null });
    try {
      const res = await apiClient.post('/projects/', payload);
      set(state => ({
        projects: [res.data, ...state.projects],
        isSaving: false,
      }));
      return { ok: true, data: res.data };
    } catch (err) {
      set({ error: err.message, isSaving: false });
      return { ok: false, error: err.message };
    }
  },

  updateProject: async (idProject, payload) => {
    set({ isSaving: true, error: null });
    try {
      const res = await apiClient.put(`/projects/${idProject}`, payload);
      set(state => ({
        projects: state.projects.map(p =>
          p.idProject === idProject ? res.data : p
        ),
        currentProject: state.currentProject?.idProject === idProject
          ? { ...state.currentProject, ...res.data }
          : state.currentProject,
        isSaving: false,
      }));
      return { ok: true, data: res.data };
    } catch (err) {
      set({ error: err.message, isSaving: false });
      return { ok: false, error: err.message };
    }
  },

  deleteProject: async (idProject) => {
    set({ isSaving: true, error: null });
    try {
      await apiClient.delete(`/projects/${idProject}`);
      set(state => ({
        projects: state.projects.filter(p => p.idProject !== idProject),
        isSaving: false,
      }));
      return { ok: true };
    } catch (err) {
      set({ error: err.message, isSaving: false });
      return { ok: false, error: err.message };
    }
  },

  copyProject: async (idProject) => {
    set({ isSaving: true, error: null });
    try {
      const res = await apiClient.post(`/projects/${idProject}/copy`);
      set(state => ({
        projects: [res.data, ...state.projects],
        isSaving: false,
      }));
      return { ok: true, data: res.data };
    } catch (err) {
      set({ error: err.message, isSaving: false });
      return { ok: false, error: err.message };
    }
  },

  // ─────────────────────────────────────────────────────────────────
  // PHASES
  // ─────────────────────────────────────────────────────────────────

  createPhase: async (idProject, payload) => {
    set({ isSaving: true, error: null });
    try {
      const res = await apiClient.post(`/projects/${idProject}/phases`, payload);
      set(state => ({
        currentProject: state.currentProject
          ? {
            ...state.currentProject,
            phases: [...(state.currentProject.phases || []), res.data]
              .sort((a, b) => a.phaseOrder - b.phaseOrder),
          }
          : state.currentProject,
        isSaving: false,
      }));
      return { ok: true, data: res.data };
    } catch (err) {
      set({ error: err.message, isSaving: false });
      return { ok: false, error: err.message };
    }
  },

  updatePhase: async (idProject, idProjectPhase, payload) => {
    set({ isSaving: true, error: null });
    try {
      const res = await apiClient.put(
        `/projects/${idProject}/phases/${idProjectPhase}`, payload
      );
      set(state => ({
        currentProject: state.currentProject
          ? {
            ...state.currentProject,
            phases: (state.currentProject.phases || [])
              .map(ph => ph.idProjectPhase === idProjectPhase ? res.data : ph)
              .sort((a, b) => a.phaseOrder - b.phaseOrder),
          }
          : state.currentProject,
        isSaving: false,
      }));
      return { ok: true, data: res.data };
    } catch (err) {
      set({ error: err.message, isSaving: false });
      return { ok: false, error: err.message };
    }
  },

  deletePhase: async (idProject, idProjectPhase) => {
    set({ isSaving: true, error: null });
    try {
      await apiClient.delete(`/projects/${idProject}/phases/${idProjectPhase}`);
      set(state => ({
        currentProject: state.currentProject
          ? {
            ...state.currentProject,
            phases: (state.currentProject.phases || [])
              .filter(ph => ph.idProjectPhase !== idProjectPhase),
          }
          : state.currentProject,
        isSaving: false,
      }));
      return { ok: true };
    } catch (err) {
      set({ error: err.message, isSaving: false });
      return { ok: false, error: err.message };
    }
  },

  // ─────────────────────────────────────────────────────────────────
  // RUNS
  // ─────────────────────────────────────────────────────────────────

  fetchRuns: async (idProject) => {
    set({ isLoadingRuns: true, error: null });
    try {
      const res = await apiClient.get(`/projects/${idProject}/runs`);
      set({ runs: res.data, isLoadingRuns: false });
    } catch (err) {
      set({ error: err.message, isLoadingRuns: false });
    }
  },

  startRun: async (idProject, payload) => {
    set({ isSaving: true, error: null });
    try {
      const res = await apiClient.post(`/projects/${idProject}/runs`, payload);
      set(state => ({
        runs: [res.data, ...state.runs],
        isSaving: false,
      }));
      return { ok: true, data: res.data };
    } catch (err) {
      set({ error: err.message, isSaving: false });
      return { ok: false, error: err.message };
    }
  },

  fetchBenchmark: async (idProject, idProjectRun) => {
    set({ isLoadingBenchmark: true, benchmark: null, error: null });
    try {
      const res = await apiClient.get(
        `/projects/${idProject}/runs/${idProjectRun}/benchmark`
      );
      set({ benchmark: res.data, isLoadingBenchmark: false });
    } catch (err) {
      set({ error: err.message, isLoadingBenchmark: false });
    }
  },

  fetchPipelinesForPicker: async () => {
    set({ isFetchingPipelines: true });
    try {
      const res = await apiClient.get('/dashboard/pipelines');
      // La respuesta del dashboard tiene { success, data: [...] }
      set({ allPipelines: res.data.data || [], isFetchingPipelines: false });
    } catch (err) {
      set({ isFetchingPipelines: false });
    }
  },

  assignPipeline: async (idProject, idProjectPhase, idConfigHead, payload) => {
    // payload = { phaseOrder: number }
    set({ isSaving: true, error: null });
    try {
      await apiClient.post(
        `/projects/${idProject}/phases/${idProjectPhase}/pipelines/${idConfigHead}`,
        payload
      );
      // Actualizar allPipelines localmente — marcar pipeline como asignado
      set(state => ({
        allPipelines: state.allPipelines.map(p =>
          p.id === idConfigHead
            ? { ...p, idProject: parseInt(idProject), idProjectPhase, phaseOrder: payload.phaseOrder }
            : p
        ),
        isSaving: false,
      }));
      return { ok: true };
    } catch (err) {
      set({ error: err.message, isSaving: false });
      return { ok: false, error: err.response?.data?.detail || err.message };
    }
  },

  unassignPipeline: async (idProject, idProjectPhase, idConfigHead) => {
    set({ isSaving: true, error: null });
    try {
      await apiClient.delete(
        `/projects/${idProject}/phases/${idProjectPhase}/pipelines/${idConfigHead}`
      );
      // Actualizar allPipelines localmente — marcar pipeline como libre
      set(state => ({
        allPipelines: state.allPipelines.map(p =>
          p.id === idConfigHead
            ? { ...p, idProject: null, idProjectPhase: null, phaseOrder: null }
            : p
        ),
        isSaving: false,
      }));
      return { ok: true };
    } catch (err) {
      set({ error: err.message, isSaving: false });
      return { ok: false, error: err.response?.data?.detail || err.message };
    }
  },

  reorderPipelineInPhase: async (idProject, idProjectPhase, idConfigHead, newPhaseOrder) => {
    // Actualiza phaseOrder del pipeline dentro de la fase
    // Reutiliza el endpoint de asignación con el nuevo orden
    set({ isSaving: true, error: null });
    try {
      await apiClient.post(
        `/projects/${idProject}/phases/${idProjectPhase}/pipelines/${idConfigHead}`,
        { phaseOrder: newPhaseOrder }
      );
      // Actualizar allPipelines localmente
      set(state => ({
        allPipelines: state.allPipelines.map(p =>
          p.id === idConfigHead ? { ...p, phaseOrder: newPhaseOrder } : p
        ),
        isSaving: false,
      }));
      return { ok: true };
    } catch (err) {
      set({ error: err.message, isSaving: false });
      return { ok: false, error: err.message };
    }
  },

  // ─────────────────────────────────────────────────────────────────
  // BULK PIPELINE CREATION
  // ─────────────────────────────────────────────────────────────────

  bulkAvailableTables: null,    // { sourceTables, targetTables, sourceEngine, targetEngine }
  bulkPreviews: null,           // { previews: [...] }
  isLoadingBulk: false,

  fetchBulkAvailableTables: async (idProject, referenceConfigHeadId) => {
    set({ isLoadingBulk: true, bulkAvailableTables: null, error: null });
    try {
      const res = await apiClient.get(
        `/projects/${idProject}/bulk-pipelines/available-tables`,
        { params: { referenceConfigHeadId } }
      );
      set({ bulkAvailableTables: res.data, isLoadingBulk: false });
      return { ok: true, data: res.data };
    } catch (err) {
      set({ error: err.message, isLoadingBulk: false });
      return { ok: false, error: err.response?.data?.detail || err.message };
    }
  },

  fetchBulkPreview: async (idProject, payload) => {
    set({ isLoadingBulk: true, bulkPreviews: null, error: null });
    try {
      const res = await apiClient.post(
        `/projects/${idProject}/bulk-pipelines/preview`, payload
      );
      set({ bulkPreviews: res.data, isLoadingBulk: false });
      return { ok: true, data: res.data };
    } catch (err) {
      set({ error: err.message, isLoadingBulk: false });
      return { ok: false, error: err.response?.data?.detail || err.message };
    }
  },

  createBulkPipelines: async (idProject, payload) => {
    set({ isSaving: true, error: null });
    try {
      const res = await apiClient.post(
        `/projects/${idProject}/bulk-pipelines/create`, payload
      );
      set({ isSaving: false });
      return { ok: true, data: res.data };
    } catch (err) {
      set({ error: err.message, isSaving: false });
      return { ok: false, error: err.response?.data?.detail || err.message };
    }
  },

  clearBulkState: () => set({
    bulkAvailableTables: null, bulkPreviews: null, isLoadingBulk: false,
  }),

  // ─────────────────────────────────────────────────────────────────
  // UI HELPERS
  // ─────────────────────────────────────────────────────────────────

  togglePhaseExpanded: (idProjectPhase) => set(state => {
    const next = new Set(state.expandedPhases);
    next.has(idProjectPhase) ? next.delete(idProjectPhase) : next.add(idProjectPhase);
    return { expandedPhases: next };
  }),

  expandAllPhases: () => set(state => ({
    expandedPhases: new Set(
      (state.currentProject?.phases || []).map(ph => ph.idProjectPhase)
    ),
  })),

  collapseAllPhases: () => set({ expandedPhases: new Set() }),

  clearError: () => set({ error: null }),
  clearBenchmark: () => set({ benchmark: null }),

  reset: () => set({
    projects: [], currentProject: null, runs: [], benchmark: null,
    isLoadingList: false, isLoadingDetail: false,
    isLoadingRuns: false, isLoadingBenchmark: false,
    isSaving: false, error: null, expandedPhases: new Set(),
    bulkAvailableTables: null, bulkPreviews: null, isLoadingBulk: false,
  }),
}));
